import { z } from 'zod';

/*
 * How chats look, per person (all their devices) and optionally per chat: the app's accent colour, the chat
 * wallpaper, bubble colours and shape, text size and spacing. Only presets and plain colours are stored,
 * never markup or URLs, so nothing here can carry content from elsewhere.
 */

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const ACCENT_PRESETS = ['#1f6feb', '#128c4b', '#7c3aed', '#e11d48', '#ea580c', '#0891b2', '#d4a017', '#334155'] as const;

/** Wallpapers: dotted (default), plain, doodles, gradients; or any plain colour. */
export const WALLPAPER_PRESETS = ['dots', 'plain', 'doodle', 'grid', 'sunset', 'ocean', 'forest', 'lavender', 'peach', 'night'] as const;
export type WallpaperPreset = (typeof WALLPAPER_PRESETS)[number];

export const wallpaperSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('preset'), preset: z.enum(WALLPAPER_PRESETS) }),
  z.object({ kind: z.literal('color'), color: hex }),
]);
export type Wallpaper = z.infer<typeof wallpaperSchema>;

const marketAppId = z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/);

/** Where the Market apps' dock sits on wide screens. */
export const DOCK_POSITIONS = ['bottom', 'left', 'right'] as const;
export type DockPosition = (typeof DOCK_POSITIONS)[number];

/** The dock's settings a person may choose over the platform's defaults. */
export const DOCK_CHOICES = ['enabled', 'position', 'autoHide'] as const;
export type DockChoice = (typeof DOCK_CHOICES)[number];

/**
 * The Market apps' dock (computers and tablets), the shelf of my apps in the Market and the home screen (/apps):
 * saved for all my devices. Ids the web app does not know (an app taken out of the catalog) are skipped, not
 * refused.
 */
export const dockSchema = z
  .object({
    /** The dock at the screen's edge: off unless turned on (Settings › Görünüm; until 0.14.1 it was on). */
    enabled: z.boolean().default(false),
    position: z.enum(DOCK_POSITIONS).default('bottom'),
    autoHide: z.boolean().default(false),
    /** The dock's apps in order; null: chosen for me (my pinned apps, else the ones I open most). */
    apps: z.array(marketAppId).max(40).nullable().default(null),
    /** Pinned to the top of my home screen. */
    pinned: z.array(marketAppId).max(60).default([]),
    /** My apps in the order I put them on the Market's shelf (pinned ones first anyway); new apps go last. */
    order: z.array(marketAppId).max(60).default([]),
    /**
     * Which of enabled, position and autoHide I chose myself; the others follow the platform's defaults
     * (PublicConfigDto.appDock). Left out on docks saved before 0.15 (see apps/web lib/dockConfig.ts).
     */
    chosen: z.array(z.enum(DOCK_CHOICES)).max(3).optional(),
  })
  .strict();
export type DockPrefs = z.output<typeof dockSchema>;
export const DEFAULT_DOCK: DockPrefs = dockSchema.parse({});

/**
 * Theme packs (the Pro Market app "Tema Paketleri"): designed sets of light and dark colours, chat bubbles and
 * a drawn wallpaper (apps/web lib/themePacks.ts). Only the pack's id and where it applies are stored; choosing
 * one needs the app (bought or added), which the server checks.
 */
export const THEME_PACK_IDS = ['gece-mavisi', 'lale', 'kapadokya', 'ege', 'orman', 'kum', 'gul-kurusu', 'grafit'] as const;
export type ThemePackId = (typeof THEME_PACK_IDS)[number];
/** 'app': the whole app takes the pack's colours; 'chats': only the chats (wallpaper and bubbles). */
export const THEME_PACK_SCOPES = ['app', 'chats'] as const;
export type ThemePackScope = (typeof THEME_PACK_SCOPES)[number];
export const themePackSchema = z.object({ id: z.enum(THEME_PACK_IDS), scope: z.enum(THEME_PACK_SCOPES).default('app') }).strict();
export type ThemePackChoice = z.output<typeof themePackSchema>;

/**
 * The social feed's view (Meydan, Nabız, Vitrin, Akış…) and how it shows, saved for all my devices. Left out until
 * first chosen (the platform's default view applies). An id the app does not know (a view switched off or removed)
 * falls back to the default; density and sort apply where the view supports them.
 */
export const viewPrefsSchema = z
  .object({
    view: z.string().regex(/^[a-z][a-z0-9-]{0,23}$/).nullable().default(null),
    density: z.enum(['comfortable', 'compact']).default('comfortable'),
    sort: z.enum(['ranked', 'latest']).default('ranked'),
    /** Calmer screens: no animations or transitions in the app (the device's own setting also applies). */
    reduceMotion: z.boolean().default(false),
  })
  .strict();
export type ViewPrefs = z.output<typeof viewPrefsSchema>;
export const DEFAULT_VIEW_PREFS: ViewPrefs = viewPrefsSchema.parse({});

export const appearanceSchema = z.object({
  /** null: the brand colour. */
  accent: hex.nullable().default(null),
  wallpaper: wallpaperSchema.default({ kind: 'preset', preset: 'dots' }),
  /** Darkens the wallpaper in dark mode, 0–80 %. */
  wallpaperDim: z.number().int().min(0).max(80).default(30),
  /** null: from the accent colour. */
  bubbleMine: hex.nullable().default(null),
  bubbleTheirs: hex.nullable().default(null),
  bubbleShape: z.enum(['sharp', 'normal', 'round']).default('normal'),
  /** Message text size in px. */
  textSize: z.number().int().min(13).max(22).default(15),
  density: z.enum(['compact', 'comfortable', 'roomy']).default('comfortable'),
  font: z.enum(['system', 'rounded', 'serif', 'mono']).default('system'),
  /** Messages of only a few emoji show large, as in WhatsApp. */
  bigEmoji: z.boolean().default(true),
  /** Left out until first changed. */
  dock: dockSchema.optional(),
  /** The theme pack in use (null or left out: none; my own colours, wallpaper and bubbles apply). */
  themePack: themePackSchema.nullable().optional(),
  /** The social feed's view and its display choices (left out until first changed). */
  views: viewPrefsSchema.optional(),
  /** Channels I follow also among my chats (Telegram style); left out until first chosen (the panel's default applies). */
  channelsInChats: z.boolean().optional(),
});
export type Appearance = z.output<typeof appearanceSchema>;
export const DEFAULT_APPEARANCE: Appearance = appearanceSchema.parse({});
export const appearanceUpdateSchema = appearanceSchema.partial().strict();

/** What one chat may set differently from the person's own look. */
export const chatAppearanceSchema = z
  .object({ wallpaper: wallpaperSchema.nullable(), bubbleMine: hex.nullable(), bubbleTheirs: hex.nullable() })
  .partial()
  .strict();
export type ChatAppearance = z.output<typeof chatAppearanceSchema>;

export interface AppearanceDto {
  appearance: Appearance;
  /** Per-chat overrides by conversation id. */
  chats: Record<string, ChatAppearance>;
}
